import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import useAuthStore from './stores/authStore';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import FirstLoginOTP from './components/FirstLoginOTP';
import Dashboard from './components/Dashboard';
import Timesheets from './components/Timesheets';
import Approvals from './components/Approvals';
import Projects from './components/Projects';
import Reports from './components/Reports';
import UserProvisioning from './components/UserProvisioning';
import SystemSettings from './components/SystemSettings';
import Layout from './components/Layout';
import Support from './components/Support';
import Profile from './components/Profile';
import NotificationsPage from './components/NotificationsPage';
import FloatingSupport from './components/FloatingSupport';
import ErrorBoundary from './components/ErrorBoundary';

const pageVariants = {
  initial: { opacity: 0, y: 10 },
  in: { opacity: 1, y: 0 },
  out: { opacity: 0, y: -10 }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.3
};

const AnimatedPage = ({ children }) => {
  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      style={{ width: '100%', height: '100%' }}
    >
      {children}
    </motion.div>
  );
};

const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isAdmin, isManager, isMember } = useAuthStore();
  
  if (!user) return <Navigate to="/" replace />;
  
  const role = user.role?.toUpperCase();
  const isAllowed = allowedRoles.some(r => {
    if (r === 'ADMIN') return isAdmin();
    if (r === 'MANAGER') return isManager();
    if (r === 'MEMBER') return isMember() || isAdmin() || isManager() || user.roles?.some(role => role.value?.toLowerCase() === 'member');
    return r === role || user.roles?.some(role => role.value?.toUpperCase() === r);
  });

  if (!isAllowed) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AnimatedRoutes = ({ isAuthenticated, user }) => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <AnimatedPage><Login /></AnimatedPage>
            )
          }
        />
        <Route path="/register" element={<AnimatedPage><Register /></AnimatedPage>} />
        <Route path="/verify-email" element={<AnimatedPage><VerifyEmail /></AnimatedPage>} />
        <Route path="/forgot-password" element={<AnimatedPage><ForgotPassword /></AnimatedPage>} />
        <Route path="/reset-password" element={<AnimatedPage><ResetPassword /></AnimatedPage>} />
        <Route path="/first-login-otp" element={<AnimatedPage><FirstLoginOTP /></AnimatedPage>} />
        <Route
          path="/*"
          element={
            isAuthenticated ? (
              <Layout>
                <AnimatePresence mode="wait">
                  <Routes location={location} key={location.pathname}>
                    <Route path="/dashboard" element={<ErrorBoundary><AnimatedPage><Dashboard /></AnimatedPage></ErrorBoundary>} />
                    <Route path="/timesheets" element={<ErrorBoundary><AnimatedPage><Timesheets /></AnimatedPage></ErrorBoundary>} />
                    <Route 
                      path="/approvals" 
                      element={
                        <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}>
                          <ErrorBoundary><AnimatedPage><Approvals /></AnimatedPage></ErrorBoundary>
                        </RoleProtectedRoute>
                      } 
                    />
                    <Route path="/projects" element={<ErrorBoundary><AnimatedPage><Projects /></AnimatedPage></ErrorBoundary>} />
                    <Route 
                      path="/reports" 
                      element={
                        <RoleProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'MEMBER']}>
                          <ErrorBoundary><AnimatedPage><Reports /></AnimatedPage></ErrorBoundary>
                        </RoleProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/users" 
                      element={
                        <RoleProtectedRoute allowedRoles={['ADMIN']}>
                          <ErrorBoundary><AnimatedPage><UserProvisioning /></AnimatedPage></ErrorBoundary>
                        </RoleProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/system-settings" 
                      element={
                        <RoleProtectedRoute allowedRoles={['ADMIN']}>
                          <ErrorBoundary><AnimatedPage><SystemSettings /></AnimatedPage></ErrorBoundary>
                        </RoleProtectedRoute>
                      } 
                    />
                    <Route path="/profile" element={<ErrorBoundary><AnimatedPage><Profile /></AnimatedPage></ErrorBoundary>} />
                    <Route path="/notifications" element={<ErrorBoundary><AnimatedPage><NotificationsPage /></AnimatedPage></ErrorBoundary>} />
                    <Route path="/support" element={<ErrorBoundary><AnimatedPage><Support /></AnimatedPage></ErrorBoundary>} />
                  </Routes>
                </AnimatePresence>
              </Layout>
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  const { isAuthenticated, fetchCurrentUser, token, user, theme: currentTheme } = useAuthStore();
  const [initializing, setInitializing] = useState(true);

  const muiTheme = React.useMemo(() => createTheme({
    palette: {
      mode: currentTheme,
      primary: {
        main: '#0d9488',
      },
      secondary: {
        main: '#0891b2',
      },
      background: {
        default: currentTheme === 'dark' ? '#0f172a' : '#f8fafc',
        paper: currentTheme === 'dark' ? '#1e293b' : '#ffffff',
      },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    },
    shape: {
      borderRadius: 8,
    },
  }), [currentTheme]);

  useEffect(() => {
    const init = async () => {
      if (token) {
        await fetchCurrentUser();
      }
      setInitializing(false);
    };
    init();
  }, [token, fetchCurrentUser]);

  if (initializing) {
    return (
      <div className="global-loader">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <BrowserRouter>
        <AnimatedRoutes isAuthenticated={isAuthenticated} user={user} />
        <FloatingSupport />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;



